import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Disc3, Music2, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VerifiedBadge from "../components/VerifiedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GenrePage() {
  const { genre } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/genres/${encodeURIComponent(genre)}`)
      .then((r) => r.json())
      .then(setData)
      .finally(() => setLoading(false));
  }, [genre]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-24 pb-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <p className="uppercase tracking-widest text-xs text-zinc-500">Gênero</p>
        <h1 className="font-heading text-5xl font-black mb-8 capitalize">{decodeURIComponent(genre)}</h1>

        {loading ? <p className="text-zinc-500">Carregando...</p> : !data ? (
          <p className="text-zinc-500">Nenhum conteúdo encontrado.</p>
        ) : (
          <div className="space-y-12">
            {data.artists?.length > 0 && (
              <section>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Users size={20} className="text-[#FF007F]"/> Artistas</h2>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-5">
                  {data.artists.map((a) => (
                    <Link key={a.id} to={`/artista/${a.slug}`} className="text-center group">
                      <div className="aspect-square rounded-full bg-zinc-900 overflow-hidden mb-2 ring-2 ring-transparent group-hover:ring-[#FF007F]/50">
                        {a.photo_url ? <img src={a.photo_url.startsWith("http") ? a.photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}`} alt={a.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-3xl font-black text-zinc-600">{a.name?.charAt(0)}</div>}
                      </div>
                      <p className="text-xs font-bold flex items-center justify-center gap-1 truncate">{a.name} {a.verified && <VerifiedBadge size={11} />}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {data.cds?.length > 0 && (
              <section>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Disc3 size={20} className="text-[#FF007F]"/> Lançamentos</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                  {data.cds.map((c) => (
                    <Link key={c.id} to={`/cd/${c.id}`} className="group block">
                      <div className="aspect-square overflow-hidden rounded-xl bg-zinc-900">
                        {c.cover_url ? <img src={c.cover_url.startsWith("http") ? c.cover_url : `${process.env.REACT_APP_BACKEND_URL}${c.cover_url}`} alt={c.cd_title} className="w-full h-full object-cover group-hover:scale-105 transition" /> : <div className="w-full h-full flex items-center justify-center text-zinc-700"><Disc3 size={36}/></div>}
                      </div>
                      <p className="text-sm font-bold mt-2 truncate">{c.cd_title}</p>
                      <p className="text-xs text-zinc-500 truncate">{c.artist_name}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {data.musics?.length > 0 && (
              <section>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Music2 size={20} className="text-[#FF007F]"/> Mais tocadas</h2>
                <ol className="divide-y divide-white/5 border border-white/10 rounded-2xl overflow-hidden">
                  {data.musics.slice(0, 20).map((m, i) => (
                    <li key={m.id} className="px-4 py-3 hover:bg-white/5 flex items-center gap-3">
                      <span className="text-zinc-500 text-sm w-6 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{m.title || "Sem título"}</p>
                        <p className="text-xs text-zinc-500 truncate">{m.artist_name}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
